class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const trigger = triggeredBy || document.querySelector('#cart-icon-bubble');
    if (this.classList.contains('active')) {
      trigger?.setAttribute('aria-expanded', 'true');
      this.loadRecommendations();
      return;
    }
    if (trigger) this.setActiveElement(trigger);
    trigger?.setAttribute('aria-expanded', 'true');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.loadRecommendations();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    this.activeElement?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  loadRecommendations() {
    const container = this.querySelector('[data-fleet-cart-recommendations]');
    if (!container || container.dataset.loaded === 'true') return;
    const url = container.dataset.url;
    if (!url) {
      container.remove();
      return;
    }
    container.dataset.loaded = 'true';
    fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: AbortSignal.timeout(10000),
    })
      .then((response) => {
        if (!response.ok) throw new Error('recommendations_unavailable');
        return response.text();
      })
      .then((html) => {
        if (!container.isConnected) return;
        const recommendations = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.fleet-recommendations');
        if (recommendations) container.replaceWith(recommendations);
        else container.remove();
      })
      .catch(() => {
        if (container.isConnected) container.remove();
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const sections = parsedState.sections || {};
    const renderedSections = new Map();
    this.getSectionsToRender().forEach((section) => {
      renderedSections.set(section.id, this.getSectionInnerHTML(sections[section.id], section.selector));
    });
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;
    this.productId = parsedState.id;
    const parsedDrawer = this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
    if (parsedDrawer) this.classList.toggle('is-empty', parsedDrawer.classList.contains('is-empty'));
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = renderedSections.get(section.id);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        const focusTarget = (focusedId && document.getElementById(focusedId)) || this.querySelector('.drawer__close');
        trapFocus(this.querySelector('.drawer__inner'), focusTarget);
        this.loadRecommendations();
      } else if (options.open !== false) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!element) throw new Error(window.fleetStrings?.refresh || window.cartStrings.error);
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
